import { MediaWithMaskBg } from '@/app/components/shared/MediaWithMaskBg';
import { MediaWithWatermark } from '@/app/components/shared/MediaWithWatermark';
import { TextElementCover } from '@/app/components/shared/TextElementCover';
import VideoLazyLoad from '@/app/components/shared/VideoLazyLoad';
import { SITE } from '@/app/constants';
import { cn, getAspectRatio } from '@/app/utils';
import type { ElementDetail } from '../types';
import { CenterPeekCarousel } from './CenterPeekCarousel';
import { CreateBy } from './CreateBy';
import { DataDisplay } from './DataDisplay';
import { TitleSection, type TitleSectionMode } from './TitleSection';

interface LeftPanelInterface {
  mode: TitleSectionMode;
  isAuthor: boolean;
  isSubmitSuccess: boolean;
  state: ElementDetail | undefined;
  referenceModalType?: 'share' | 'reference' | null;
  onReport: () => void;
  handleShareClick: () => void;
}

const DEFAULT_ASPECT_RATIO = 318 / 485;

export const LeftPanel = ({
  mode,
  isAuthor,
  isSubmitSuccess,
  onReport,
  state,
  referenceModalType,
  handleShareClick,
}: LeftPanelInterface) => {
  const firstCreation = state?.creations?.[0];
  const components = state?.components || [];
  const isShare = referenceModalType === 'share';
  const modality = state?.modality;
  const hasMultipleComponents = components.length > 1;
  const showWatermark = SITE === 'china' && state?.source === 'element_task';

  // 共享主体的视频
  const renderCreationMedia = (creation: typeof firstCreation, index?: number) => {
    if (!creation?.cover_uri || !creation?.uri) return null;

    return (
      <VideoLazyLoad
        key={creation.uri || index}
        poster={creation.cover_uri}
        videoSrc={creation.uri}
        className="h-auto max-h-[485px] w-auto max-w-[318px] overflow-hidden rounded-16 border border-system-white16"
        wrapperStyle={{
          aspectRatio:
            creation.resolution?.width && creation.resolution?.height
              ? getAspectRatio({
                  size: {
                    width: creation.resolution.width,
                    height: creation.resolution.height,
                  },
                })
              : DEFAULT_ASPECT_RATIO,
        }}
        videoClassName="object-cover"
        priority={index === 0}
        hoverPlay={false}
      />
    );
  };

  const renderComponentReference = (component: (typeof components)[0], index?: number) => {
    const contentType = component.content_type;
    const imageUrl = component.src_img || (contentType === 'image' ? component.content : undefined);
    const { videoUrl, coverUrl } =
      contentType === 'video'
        ? { videoUrl: component.content, coverUrl: component.cover_uri }
        : { videoUrl: undefined, coverUrl: undefined };

    if (contentType === 'image' && imageUrl) {
      return (
        <div key={imageUrl || index} className="relative size-[160px]">
          <MediaWithMaskBg
            type="image"
            src={imageUrl}
            maskSrc={imageUrl}
            alt="reference"
            className="size-full rounded-16 border border-system-white16 object-contain"
            fullScreen
            fullScreenWatermark={showWatermark}
          />
          {showWatermark && <MediaWithWatermark overlay width={1} height={1} showWatermark />}
        </div>
      );
    }

    if (contentType === 'video' && videoUrl) {
      return (
        <VideoLazyLoad
          key={videoUrl || index}
          poster={coverUrl || ''}
          videoSrc={videoUrl}
          className="size-[160px] overflow-hidden rounded-16 border border-system-white16"
          wrapperStyle={{
            aspectRatio: 1,
          }}
          videoClassName="size-full object-cover"
          priority={index === 0}
          hoverPlay={false}
        />
      );
    }
    return null;
  };

  const backgroundImage = isShare && firstCreation?.cover_uri ? firstCreation.cover_uri : undefined;

  return (
    <div
      className={cn(
        'relative flex flex-1 flex-col overflow-hidden px-4 pt-3 pb-4 sm:w-full sm:bg-none sm:p-0 sm:px-4',
        !isShare && 'bg-system-black',
      )}
      style={{
        ...(backgroundImage
          ? {
              backgroundImage: `url(${backgroundImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }
          : {}),
      }}
    >
      {backgroundImage && <div className="absolute inset-0 bg-system-black/70 backdrop-blur-[50px]" />}
      <div className="relative flex flex-1 flex-col">
        <TitleSection
          className="sm:hidden"
          mode={mode}
          isAuthor={isAuthor}
          onReport={onReport}
          shareStatus={state?.shareStatus}
          handleShareClick={handleShareClick}
        />
        <div
          className={cn(
            'mt-4 py-4',
            isShare ? 'flex flex-1 flex-col items-center justify-center px-6' : 'mb-3 h-full p-0',
          )}
        >
          {/* 共享主体：只显示第一个示例视频 */}
          {isShare && firstCreation && renderCreationMedia(firstCreation, 0)}
          {/* 非共享主体：根据 modality 判断渲染方式 */}
          {!isShare &&
            (modality === 'text' ? (
              <div className="flex h-full items-center justify-center">
                <TextElementCover name={state?.name} className="size-[160px] rounded-16" />
              </div>
            ) : modality === 'image' || modality === 'video' ? (
              hasMultipleComponents ? (
                <CenterPeekCarousel
                  items={components.map((component, index) => (
                    <div
                      key={component.content || component.src_img || `component-${index}`}
                      className="flex items-center justify-center"
                    >
                      {renderComponentReference(component, index)}
                    </div>
                  ))}
                  className="size-full"
                />
              ) : (
                components[0] && (
                  <div className="flex h-full items-center justify-center">
                    {renderComponentReference(components[0], 0)}
                  </div>
                )
              )
            ) : null)}
        </div>
        <div className={cn('flex-col items-center justify-center gap-3 px-6 pb-4', isShare ? 'flex' : 'hidden')}>
          <CreateBy isSubmitSuccess={isSubmitSuccess} creator={state?.creator} />

          <DataDisplay
            isSubmitSuccess={isSubmitSuccess}
            shareUseCount={state?.shareUseCount}
            allowCopy={state?.allowCopy}
            collectCount={state?.reactInfo?.collect_count}
            isAuthor={isAuthor}
            shareCopyCount={state?.shareCopyCount}
          />
        </div>
      </div>
    </div>
  );
};
